import React, { useState, useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Logo from "../image/AgriHyphen AI.png";
import { languageFlags } from "../i18n";
import { getStoredLanguage, setStoredLanguage } from "../languageUtils";
import { ArrowUpRight, Menu as MenuIcon, Close as CloseIcon } from "./ui/Icons";

const NAV_ITEMS = [
  { key: "nav1", path: "/" },
  { key: "nav3", path: "/team" },
  { key: "Climate Action", path: "/climate-action" },
  { key: "Blogs", path: "/blogs" },
  { key: "aboutUs", path: "/info" },
  { key: "Donate", path: "/donate" },
];

/* Routes whose hero is a dark photograph — the bar starts transparent there
   and only takes on a solid ground once the user scrolls past it. */
const DARK_HERO_ROUTES = ["/"];

export default function Navbar() {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const langRef = useRef(null);

  useEffect(() => {
    const stored = getStoredLanguage();
    if (stored && stored !== i18n.language) i18n.changeLanguage(stored);
  }, [i18n]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 32);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const onDoc = (e) => {
      if (langRef.current && !langRef.current.contains(e.target))
        setLangOpen(false);
    };
    const onEsc = (e) => {
      if (e.key === "Escape") {
        setLangOpen(false);
        setMobileOpen(false);
      }
    };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onEsc);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onEsc);
    };
  }, []);

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    document.body.style.overflow = mobileOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [mobileOpen]);

  const onPickLanguage = (code) => {
    setStoredLanguage(code);
    i18n.changeLanguage(code);
    setLangOpen(false);
  };

  const isActive = (path) =>
    path === "/"
      ? location.pathname === "/"
      : location.pathname.startsWith(path.split("#")[0]);

  const getNavLabel = (item) => item.label || t(item.key);

  const currentLang = languageFlags[i18n.language] || languageFlags.en;
  const overDarkHero = DARK_HERO_ROUTES.includes(location.pathname) && !scrolled;

  return (
    <>
      <header
        className={`fixed inset-x-0 top-0 z-50 transition-[background-color,border-color,backdrop-filter] duration-500 ease-out ${
          overDarkHero
            ? "border-b border-white/10 bg-transparent"
            : "border-b border-agri-rule bg-agri-paper/92 backdrop-blur-md"
        }`}
      >
        {/* Over the hero photograph the bar has no ground of its own, so a
            short top scrim keeps white nav text above 4.5:1 regardless of
            what the image is doing underneath. */}
        {overDarkHero && (
          <div
            aria-hidden="true"
            className="pointer-events-none absolute inset-x-0 top-0 h-24 bg-gradient-to-b from-agri-forestDeep/80 to-transparent"
          />
        )}
        <div className="container-x relative">
          <div className="flex h-[var(--nav-h)] items-center justify-between gap-6">
            {/* Wordmark set in the display serif — the site's signature */}
            <Link
              to="/"
              className={`flex shrink-0 items-center gap-3 transition-colors duration-300 ${
                overDarkHero ? "text-white" : "text-agri-ink"
              }`}
            >
              <img
                src={Logo}
                alt=""
                aria-hidden="true"
                className="h-8 w-8 object-contain"
              />
              <span className="font-display text-[1.0625rem] font-semibold tracking-[-0.02em]">
                AgriHyphen<span className="text-agri-clay"> AI</span>
              </span>
            </Link>

            <nav className="hidden items-center gap-1 lg:flex">
              {NAV_ITEMS.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  aria-current={isActive(item.path) ? "page" : undefined}
                  className={`relative px-3 py-2 text-[0.875rem] font-medium transition-colors duration-200 ${
                    overDarkHero
                      ? isActive(item.path)
                        ? "text-white"
                        : "text-white/65 hover:text-white"
                      : isActive(item.path)
                        ? "text-agri-ink"
                        : "text-agri-ink60 hover:text-agri-ink"
                  }`}
                >
                  {getNavLabel(item)}
                  {/* Active state is a rule under the label, not a grey pill */}
                  <span
                    className={`absolute inset-x-3 -bottom-px h-px transition-opacity duration-200 ${
                      isActive(item.path) ? "opacity-100" : "opacity-0"
                    } ${overDarkHero ? "bg-agri-lime" : "bg-agri-clay"}`}
                  />
                </Link>
              ))}
            </nav>

            <div className="hidden shrink-0 items-center gap-2 lg:flex">
              <div className="relative" ref={langRef}>
                <button
                  type="button"
                  onClick={() => setLangOpen((v) => !v)}
                  aria-label={`Language: ${currentLang.label}`}
                  aria-expanded={langOpen}
                  className={`inline-flex h-11 items-center gap-2 px-2.5 text-[0.75rem] font-medium transition-colors duration-200 ${
                    overDarkHero
                      ? "text-white/70 hover:text-white"
                      : "text-agri-ink60 hover:text-agri-ink"
                  }`}
                >
                  <img
                    src={currentLang.flag}
                    alt=""
                    aria-hidden="true"
                    className="h-5 w-5 rounded-full object-cover"
                  />
                  <span className="font-mono uppercase tracking-[0.08em]">
                    {i18n.language.slice(0, 2)}
                  </span>
                </button>
                {langOpen && (
                  <div className="absolute right-0 top-full mt-2 w-44 border border-agri-rule bg-agri-card shadow-lift">
                    {Object.entries(languageFlags).map(
                      ([code, { flag, label }]) => (
                        <button
                          key={code}
                          onClick={() => onPickLanguage(code)}
                          className={`flex w-full items-center gap-2.5 px-4 py-3 text-left text-[0.8125rem] transition-colors ${
                            i18n.language === code
                              ? "bg-agri-paperDeep font-medium text-agri-ink"
                              : "text-agri-ink60 hover:bg-agri-paper hover:text-agri-ink"
                          }`}
                        >
                          <img
                            src={flag}
                            alt=""
                            aria-hidden="true"
                            className="h-4 w-4 rounded-full"
                          />
                          {label}
                        </button>
                      )
                    )}
                  </div>
                )}
              </div>

              <Link
                to="/test-ai"
                className={
                  overDarkHero
                    ? "btn-lime !min-h-[2.5rem] !px-5"
                    : "btn-primary !min-h-[2.5rem] !px-5"
                }
              >
                {t("testOurAI")}
                <ArrowUpRight className="h-3.5 w-3.5" />
              </Link>
            </div>

            <button
              type="button"
              onClick={() => setMobileOpen(true)}
              className={`-mr-2 inline-flex h-11 w-11 items-center justify-center transition-colors duration-200 lg:hidden ${
                overDarkHero ? "text-white" : "text-agri-ink"
              }`}
              aria-label="Open menu"
            >
              <MenuIcon />
            </button>
          </div>
        </div>
      </header>

      {/* Mobile drawer */}
      <div
        className={`fixed inset-0 z-[60] lg:hidden ${
          mobileOpen ? "pointer-events-auto" : "pointer-events-none"
        }`}
        aria-hidden={!mobileOpen}
      >
        <div
          className={`absolute inset-0 bg-agri-forestDeep/70 transition-opacity duration-300 ${
            mobileOpen ? "opacity-100" : "opacity-0"
          }`}
          onClick={() => setMobileOpen(false)}
        />
        <div
          className={`absolute inset-y-0 right-0 flex w-[86%] max-w-sm flex-col bg-agri-paper transition-transform duration-300 ease-out ${
            mobileOpen ? "translate-x-0" : "translate-x-full"
          }`}
        >
          <div className="flex h-[var(--nav-h)] shrink-0 items-center justify-between border-b border-agri-rule px-6">
            <span className="font-display text-[1.0625rem] font-semibold tracking-[-0.02em] text-agri-ink">
              AgriHyphen<span className="text-agri-clay"> AI</span>
            </span>
            <button
              type="button"
              onClick={() => setMobileOpen(false)}
              className="-mr-2 inline-flex h-11 w-11 items-center justify-center text-agri-ink60 transition-colors hover:text-agri-ink"
              aria-label="Close menu"
            >
              <CloseIcon />
            </button>
          </div>

          <nav className="flex-1 overflow-y-auto px-6 py-4">
            {NAV_ITEMS.map((item, i) => (
              <Link
                key={item.path}
                to={item.path}
                className={`flex items-baseline gap-4 border-b border-agri-ruleSoft py-4 font-display text-[1.25rem] font-semibold tracking-[-0.02em] transition-colors ${
                  isActive(item.path)
                    ? "text-agri-clay"
                    : "text-agri-ink hover:text-agri-clay"
                }`}
              >
                <span className="font-mono text-[0.625rem] font-medium tabular-nums text-agri-ink40">
                  {String(i + 1).padStart(2, "0")}
                </span>
                {getNavLabel(item)}
              </Link>
            ))}
          </nav>

          <div className="shrink-0 space-y-4 border-t border-agri-rule px-6 pb-8 pt-5">
            <Link to="/test-ai" className="btn-primary w-full">
              {t("testOurAI")}
              <ArrowUpRight className="h-3.5 w-3.5" />
            </Link>
            <div>
              <p className="kicker mb-3">Language</p>
              <div className="grid grid-cols-2 gap-2">
                {Object.entries(languageFlags).map(([code, { flag, label }]) => (
                  <button
                    key={code}
                    onClick={() => onPickLanguage(code)}
                    className={`flex min-h-[2.75rem] items-center gap-2 border px-3 text-[0.75rem] font-medium transition-colors ${
                      i18n.language === code
                        ? "border-agri-ink bg-agri-ink text-agri-paper"
                        : "border-agri-rule text-agri-ink60 hover:border-agri-ink hover:text-agri-ink"
                    }`}
                  >
                    <img
                      src={flag}
                      alt=""
                      aria-hidden="true"
                      className="h-4 w-4 rounded-full"
                    />
                    {label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
